import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const CartContext = createContext(null);
const CART_KEY = 'ncl_cart_items';

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; }
    catch { return []; }
  });

  // Persist to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product) => {
    setItems(prev => {
      const existing = prev.find(i => i.name === product.name);
      if (existing) {
        return prev.map(i =>
          i.name === product.name ? { ...i, qty: i.qty + 1 } : i
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
  }, []);

  const removeItem = useCallback((name) => {
    setItems(prev => prev.filter(i => i.name !== name));
  }, []);

  const updateQty = useCallback((name, qty) => {
    if (qty < 1) {
      setItems(prev => prev.filter(i => i.name !== name));
      return;
    }
    setItems(prev => prev.map(i => i.name === name ? { ...i, qty } : i));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(CART_KEY);
  }, []);

  const total = items.reduce((sum, i) => {
    const price = parseFloat(String(i.price || '0').replace(/[^0-9.]/g, ''));
    return sum + price * i.qty;
  }, 0);

  const count = items.reduce((sum, i) => sum + i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, total, count }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
